import { createSelector } from 'reselect';

import { GRACE_EXPIRY_DAYS, isTestnet } from 'constants/constants';
import { TRADE_PAGE_VERIFICATION_FLAGS } from 'constants/enums';
import {
  GRACE_KYC_EXPIRY_AVAILABLE,
  IMPORT_KYC_FEATURE_ENABLED,
} from 'constants/featureFlag';
import { getDateTime, timeNow } from 'helpers/day';
import { shouldDisplayKycCard } from 'helpers/user';
import {
  isUserVerified,
  isKycRefreshExpired as checkKycRefreshExpired,
  isUserNonIndianAndReduceOnly,
  isKycRefreshExpiryDateCrossed,
  isKycRequiredDeadlineCrossed,
} from 'helpers/utils';
import IState from 'types/Istore';
import { PROOF_STATUS } from 'types/IUser';

import {
  isUserIndianSelector,
  isUserKycDoneSelector,
  userSelector,
  userProfileSelector,
  userAddressProofSelector,
} from './otherSelectors';

const selectUserReferralCode = createSelector([userSelector], user => user.referral_code);

const selectIsUserSubAccount = createSelector([userSelector], user =>
  Boolean(user.is_sub_account)
);

const selectIsVerifiedIndianUser = createSelector(
  [isUserIndianSelector, isUserKycDoneSelector],
  (isIndian, isKycDone) => isIndian && isKycDone
);

const selectUserAccountName = createSelector([userSelector], user => user.account_name);

const selectMfaCheckForSubAndMainAccount = createSelector([userSelector], user => {
  if (user.is_sub_account) {
    if (user.main_account?.email) {
      return user.main_account?.isMfaEnabled;
    }
  }
  return user.isMfaEnabled;
});

const selectActiveSubAccountEmailEqualsMainAccountEmail = createSelector(
  [userSelector],
  user => {
    if (user.is_sub_account) {
      return user.main_account?.email === user.email;
    }
    return false;
  }
);

/**
 * @description This will give the referral code used during signup process of the user.
 * It can be some custom referral code or the default referral code.
 */
const selectSignupReferralCode = createSelector([userProfileSelector], profile => {
  return profile?.signup_referral_code;
});

const selectUserPreference = (state: IState) => state.user.preferences;

const isUserLevelOneVerifiedSelector = createSelector(
  [userSelector],
  user => user.phone_verification_status === 'verified'
);

const isUserLevelTwoVerifiedSelector = createSelector(
  [userSelector],
  user => user.proof_of_identity_status === 'approved'
);

const isUserLevelThreeVerifiedSelector = createSelector(
  [userSelector],
  user => user.proof_of_address_status === 'approved'
);

const onlyLevel2VerifiedSelector = createSelector(
  [isUserLevelTwoVerifiedSelector, isUserLevelThreeVerifiedSelector],
  (levelTwoVerified, levelThreeVerified) => levelTwoVerified && !levelThreeVerified
);
const selectUserPhoneVerificationStatus = (state: IState) =>
  state.user.phone_verification_status;

// When user is reactivated after  deletion of account and phone is unverified, we should ask the user to verify the phone
const selectShouldAskReActivatedUserToVerifyPhone = createSelector(
  [isUserKycDoneSelector, selectUserPhoneVerificationStatus, selectIsUserSubAccount],
  (isKycDone, phoneVerificationStatus, isSubAccount) => {
    if (isSubAccount) {
      return false;
    }
    return Boolean(isKycDone) && phoneVerificationStatus === 'unverified';
  }
);

const selectUserGlobalKycData = (state: IState) => state.user.userGlobalKycData;

const selectShowDoKycCard = createSelector([userSelector], user => {
  const {
    profile,
    is_kyc_done: isKycDone,
    userGlobalKycData,
    kycShareConsentPopupConfig,
  } = user;
  const { login_count: loginCount } = profile ?? {};

  if (!userGlobalKycData && IMPORT_KYC_FEATURE_ENABLED) return false;

  if (kycShareConsentPopupConfig?.show) {
    return false;
  }

  return Boolean(shouldDisplayKycCard(isKycDone, loginCount));
});

const selectUserEmailSha256 = (state: IState) => state.user.sha256EmailHexString;

const selectIsKycRefreshRequired = (state: IState) => {
  return state.user.is_kyc_refresh_required;
};

const selectKycExpiryDate = (state: IState) => state.user.kyc_expiry_date;

const selectKycRefreshProcessPending = createSelector(
  [isUserKycDoneSelector, userAddressProofSelector, selectIsKycRefreshRequired],
  (isKycDone, proofOfAddress, isKycRefreshRequired) => {
    return !isKycDone && isKycRefreshRequired && proofOfAddress === PROOF_STATUS.PENDING;
  }
);

export const isUserVerifiedSelector = createSelector(
  [isUserKycDoneSelector, selectIsKycRefreshRequired, selectKycExpiryDate],
  (isKycDone, isKycRefreshRequired, kycExpiryDate) =>
    isUserVerified({
      is_kyc_done: isKycDone,
      is_kyc_refresh_required: isKycRefreshRequired,
      kyc_expiry_date: kycExpiryDate,
    })
);

const selectGraceDateAfterKycExpiry = createSelector(
  [selectKycExpiryDate],
  kycExpiryDate => {
    if (!kycExpiryDate)
      return { graceDate: null, daysLeft: null, isGracePeriodCrossed: false };
    const graceDate = getDateTime(kycExpiryDate).add(GRACE_EXPIRY_DAYS, 'day');
    const daysLeft = graceDate.diff(timeNow(), 'days', true);
    return {
      graceDate,
      daysLeft,
      isGracePeriodCrossed: graceDate.valueOf() < timeNow().valueOf(),
    };
  }
);

// only for indian exchange
const selectDepositAndWithdrawalVerificationRequired = (state: IState) => {
  const isKycDone = state.user.is_kyc_done;
  const isKycRefreshRequired = state.user.is_kyc_refresh_required;

  return !isKycDone && !isKycRefreshRequired;
};

const selectIsKycRefreshExpired = createSelector(
  [isUserKycDoneSelector, selectIsKycRefreshRequired, selectKycExpiryDate],
  (isKycDone, isKycRefreshRequired, kycExpiryDate) => {
    return checkKycRefreshExpired({
      is_kyc_done: isKycDone,
      is_kyc_refresh_required: isKycRefreshRequired,
      kyc_expiry_date: kycExpiryDate,
    });
  }
);

const selectIsUserInReduceOnlyMode = createSelector(
  [userSelector, selectGraceDateAfterKycExpiry, selectIsKycRefreshExpired],
  (user, { graceDate }, isKycRefreshExpired) => {
    const {
      is_kyc_refresh_required: isKycRefreshRequired,
      kyc_expiry_date: kycExpiryDate,
      is_kyc_done: isKycDone,
    } = user;

    if (isTestnet) return false;

    if (GRACE_KYC_EXPIRY_AVAILABLE) {
      const isKycRefreshGracePeriodExpired =
        graceDate && graceDate.valueOf() < timeNow().valueOf();
      if (
        !isKycDone &&
        isKycRefreshRequired &&
        isKycRefreshExpired &&
        !isKycRefreshGracePeriodExpired
      )
        return true;

      // user is newly unverified or grace period over and trading is restricted
      return false;
    }

    // no grace period here always reduce only after expiry
    return (
      isUserNonIndianAndReduceOnly(user) ||
      isKycRequiredDeadlineCrossed(user) ||
      isKycRefreshExpiryDateCrossed(kycExpiryDate)
    );
  }
);

// TODO: logic currently being implemented for indian exchange. Global should also be added here currently present in multiple places
const selectTradePagesVerificationMessageFlag = createSelector(
  [
    isUserKycDoneSelector,
    selectIsUserInReduceOnlyMode,
    selectIsKycRefreshRequired,
    selectIsKycRefreshExpired,
  ],
  (isUserKycDone, isUserinReduceOnlyMode, isKycRefreshRequired, isKycRefreshExpired) => {
    if (isTestnet) return null;
    if (GRACE_KYC_EXPIRY_AVAILABLE) {
      if (isUserinReduceOnlyMode) return TRADE_PAGE_VERIFICATION_FLAGS.REDUCE_ONLY;

      // refresh kyc required case
      if (!isUserKycDone && isKycRefreshRequired && isKycRefreshExpired) {
        return TRADE_PAGE_VERIFICATION_FLAGS.GET_VERIFIED;
      }

      if (!isUserKycDone && !isKycRefreshRequired) {
        return TRADE_PAGE_VERIFICATION_FLAGS.GET_VERIFIED;
      }
    }

    return null;
  }
);

const selectUserFirstTradeTime = createSelector([userProfileSelector], profile => {
  return profile?.first_trades_data?.first_trade_time;
});

const selectIsUserSubAccountAndMainAccountDataPresent = (state: IState) => {
  return state.user.is_sub_account && Boolean(state.user.main_account?.email);
};

const selectUserMainAccountEmail = (state: IState) => state.user.main_account?.email;

export {
  selectUserReferralCode,
  selectIsUserSubAccount,
  selectIsVerifiedIndianUser,
  selectUserAccountName,
  selectMfaCheckForSubAndMainAccount,
  selectSignupReferralCode,
  selectShouldAskReActivatedUserToVerifyPhone,
  selectUserPhoneVerificationStatus,
  selectActiveSubAccountEmailEqualsMainAccountEmail,
  selectUserPreference,
  isUserLevelOneVerifiedSelector,
  isUserLevelTwoVerifiedSelector,
  isUserLevelThreeVerifiedSelector,
  onlyLevel2VerifiedSelector,
  selectUserGlobalKycData,
  selectShowDoKycCard,
  selectUserEmailSha256,
  selectGraceDateAfterKycExpiry,
  selectIsKycRefreshRequired,
  selectKycRefreshProcessPending,
  selectIsKycRefreshExpired,
  selectDepositAndWithdrawalVerificationRequired,
  selectTradePagesVerificationMessageFlag,
  selectIsUserInReduceOnlyMode,
  selectUserFirstTradeTime,
  selectIsUserSubAccountAndMainAccountDataPresent,
  selectUserMainAccountEmail,
};
